import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";

/* Depois de salvar com sucesso um formulário de página inteira, cada lado volta para a SUA home
   (decisão do dono, 08/10/2026): o cliente para `/app`, o time interno para `/admin`. A regra
   mora aqui — as telas só dizem a frase de sucesso. O lado vem do endereço em que a pessoa está
   (`/admin/**` é o console; o resto da área com sessão é a do cliente), o mesmo corte que o
   roteador usa para exigir o papel interno. */

export const HOME_DO_CLIENTE = "/app";
export const HOME_INTERNA = "/admin";

/** Estado da navegação que carrega o aviso de sucesso até a home. */
export interface EstadoDoRetorno {
  avisoDeSucesso: string;
}

/** A home do lado em que o endereço está. */
export function homeDoEndereco(pathname: string): string {
  return pathname === HOME_INTERNA || pathname.startsWith(`${HOME_INTERNA}/`)
    ? HOME_INTERNA
    : HOME_DO_CLIENTE;
}

/** Lê o aviso do estado da navegação, ou `null` quando a chegada não veio de um salvamento. */
export function avisoDoEstado(estado: unknown): string | null {
  if (typeof estado !== "object" || estado === null) return null;
  const aviso = (estado as Partial<EstadoDoRetorno>).avisoDeSucesso;
  return typeof aviso === "string" && aviso.trim() ? aviso : null;
}

/**
 * Devolve a função que leva a pessoa à home do lado dela com um aviso de sucesso curto.
 * Chame só no sucesso de formulário de página inteira: falha mantém a pessoa no formulário,
 * e edição em linha numa lista não sai da lista.
 */
export function useVoltarParaHome(): (aviso: string) => void {
  const { pathname } = useLocation();
  const navegar = useNavigate();
  return useCallback(
    (aviso: string) => {
      const estado: EstadoDoRetorno = { avisoDeSucesso: aviso };
      void navegar(homeDoEndereco(pathname), { state: estado });
    },
    [navegar, pathname],
  );
}

/**
 * O aviso de sucesso que a home deve mostrar, UMA vez: lido do estado da navegação e logo
 * apagado dele, para que recarregar a página ou voltar a ela não o repita.
 */
export function useAvisoDeSucessoDaChegada(): string | null {
  const { pathname, search, state } = useLocation();
  const navegar = useNavigate();
  const vindo = avisoDoEstado(state);
  const [aviso, setAviso] = useState<string | null>(vindo);
  // Ajuste de estado durante a renderização (padrão do React): o aviso que chegou fica guardado
  // aqui mesmo depois de o estado da navegação ser apagado logo abaixo.
  if (vindo !== null && vindo !== aviso) setAviso(vindo);

  useEffect(() => {
    if (vindo === null) return;
    void navegar(`${pathname}${search}`, { replace: true, state: null });
  }, [vindo, pathname, search, navegar]);

  return vindo ?? aviso;
}
